import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { TemplateRef } from '@angular/core';
import { ActionIconDef, ColumnAlign, GridConfig, GridRow, GridRowProblem, GridRowTone, ToolbarActionDef } from '@inavista/components/grid';

// Claim popup → Service Order Claims (the claim lines) on @inavista/components. The columns stay the component's
// Syncfusion column list (`columns`): labels, widths, formats, default visibility and the cell editors (each column's
// `edit`, drawn by CellEditorHost) come from it, so the price, total, tax and ISC-rule logic in those editors runs as
// before. Rows are the claim form's own `iscClaimDetails` (LocalRowsGridApi, no endpoint), saved with the popup's Save.

export const CLAIM_LINES_URL = 'page://claim-lines';
export const CLAIM_LINES_PERSISTENCE_KEY = 'rims.service.claim-popup-lines';

export const ADD_LINE_ACTION = 'add-item';
export const APPROVE_ALL_ACTION = 'approve-all';
export const REJECT_ALL_ACTION = 'reject-all';
export const FLAG_ALL_ACTION = 'flag-all';
export const INCLUDE_TAX_ACTION = 'include-tax';
/** A hidden field the page writes when a line opens, so leaving it always saves it (the Syncfusion grid saved on leave). */
export const LINE_OPEN_FIELD = '__open';

const BLACK = '#000000de';
const INACTIVE = '#cccccc';
const ORANGE = '#ff8403';
const PLUS_ICON = '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />';
const CHECK_ICON = '<polyline points="20 6 9 17 4 12" />';
const CROSS_ICON = '<line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />';
const FLAG_ICON = '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />';
const TRASH_ICON = '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />';
const COMMENT_ICON = '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />';
const TAX_ICON = '<rect x="3" y="3" width="18" height="18" rx="2" /><polyline points="7 12 10 15 17 8" />';

/** The old row colours by line status (queryCellInfo). */
const STATUS_TONES: Record<string, string> = { Approved: '#c0ffc0', Rejected: '#ffa8a8', Flagged: '#fbff0080' };

export interface ClaimLinesGridContext {
  /** The Syncfusion column list. */
  columns: any[];
  editors: Record<string, TemplateRef<unknown>>;
  canExport: boolean;
  /** The old toolbar items the user's role and the claim status allow, in their old order. */
  canAdd: boolean;
  canApproveAll: boolean;
  canRejectAll: boolean;
  canFlagAll: boolean;
  /** The Include Tax checkbox: shown, its value, and why it is locked (national account without the ISC tax rule). */
  showIncludeTax: boolean;
  includeTax: boolean;
  includeTaxLockReason: string | null;
  editLockReason: (row: GridRow) => string | null;
  validate: (row: GridRow) => GridRowProblem | null;
}

/** Every column but the old command column. */
export function claimLineFields(columns: any[]): any[] {
  return columns.filter(c => c.field && !c.commands);
}

/** Hidden and kept out of the column chooser: backing fields (id, status), never offered. */
function isBacking(c: any) {
  return c.visible === false && c.showInColumnChooser === false;
}

/** The cells drawn with the column's editor. */
export function claimLineEditFields(columns: any[]): string[] {
  return claimLineFields(columns).filter(c => c.edit && !isBacking(c) && c.allowEditing !== false).map(c => c.field);
}

const RIGHT = ['quantity', 'mileage', 'price', 'discountAmount', 'totalAmount', 'taxPercent', 'taxAmount'];

export function buildClaimLinesGridConfig(ctx: ClaimLinesGridContext): GridConfig {
  const fields = claimLineFields(ctx.columns);
  const shown = fields.filter(c => !isBacking(c));
  const align: Record<string, ColumnAlign & { header?: ColumnAlign }> = {};
  // RIMS grid look: headers left; text left; the line number centred; amounts and quantities right.
  for (const c of shown) {
    align[c.field] = { horizontal: c.field === 'rowId' ? 'center' : RIGHT.includes(c.field) ? 'right' : 'left', header: { horizontal: 'left' } };
  }
  const toolbarActions: ToolbarActionDef[] = [];
  if (ctx.canAdd) toolbarActions.push({ key: ADD_LINE_ACTION, label: 'Add Claim Item', kind: 'button', icon: PLUS_ICON });
  if (ctx.canApproveAll) toolbarActions.push({ key: APPROVE_ALL_ACTION, label: 'Approve All', kind: 'button', icon: CHECK_ICON, showLabel: true, position: 'end' });
  if (ctx.canRejectAll) toolbarActions.push({ key: REJECT_ALL_ACTION, label: 'Reject All', kind: 'button', icon: CROSS_ICON, showLabel: true, position: 'end' });
  if (ctx.canFlagAll) toolbarActions.push({ key: FLAG_ALL_ACTION, label: 'Flag All', kind: 'button', icon: FLAG_ICON, showLabel: true, position: 'end' });
  // The claim's own setting (saved with the claim), so never a stored layout preference.
  if (ctx.showIncludeTax) toolbarActions.push({
    key: INCLUDE_TAX_ACTION, label: 'Include Tax', kind: 'toggle', icon: TAX_ICON, showLabel: true, position: 'end', persist: false,
    defaultValue: ctx.includeTax, disabled: !!ctx.includeTaxLockReason, disabledReason: ctx.includeTaxLockReason ?? '',
  });
  return {
    apiBaseUrl: CLAIM_LINES_URL,
    clientSide: true,
    keyColumn: 'rowId',
    rowHeight: 26,
    defaultColumnOrder: shown.map(c => c.field),
    // lineCommentCount: the Comment icon's colour only.
    hiddenByDefaultColumns: [...fields.filter(isBacking).map(c => c.field), 'lineCommentCount', LINE_OPEN_FIELD],
    startHiddenColumns: shown.filter(c => c.visible === false).map(c => c.field),
    // The old grid kept these out of its column chooser while showing them.
    alwaysVisibleColumns: shown.filter(c => c.showInColumnChooser === false && c.visible !== false).map(c => c.field),
    columnLabels: Object.fromEntries(shown.map(c => [c.field, c.headerText])),
    columnWidths: Object.fromEntries(shown.filter(c => c.width).map(c => [c.field, +c.width])),
    columnAlign: align,
    columnNumberFormats: Object.fromEntries(RIGHT.map(f => [f, { minimumFractionDigits: 2, maximumFractionDigits: 2 }])),
    columnTypes: { lastUpdatedDate: 'datetime' },
    columnDateFormats: { lastUpdatedDate: 'd MMM, yyyy h:mm a' },
    // Approved green, Rejected red, Flagged yellow, as before.
    rowToneFn: row => (STATUS_TONES[row['status'] as string] ? { bg: STATUS_TONES[row['status'] as string] } as GridRowTone : null),
    excelFilterColumns: shown.filter(c => c.filter?.type === 'Excel').map(c => c.field),
    // Item and Description open the item, as before.
    hyperlinkColumns: ['item', 'description'],
    enableExport: ctx.canExport,
    enableColumnsPicker: true,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    editMode: 'inline',
    editSaveMode: 'row',
    // A double click opens the row, as before.
    editTrigger: 'doubleClick',
    // None while the claim's status locks its lines (the old editSettings.allowEditing): a double click opens nothing.
    editableColumns: ctx.editLockReason({}) ? [] : claimLineEditFields(ctx.columns),
    editorTemplates: ctx.editors,
    readOnlyCellFn: row => !!ctx.editLockReason(row),
    // The Syncfusion validation rules of the columns (required), read from the open editors.
    rowValidator: row => ctx.validate(row),
    deleteMode: 'host',
    rowDeletable: () => false,
    toolbarActions,
    emptyStateMessage: 'No claim lines.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CLAIM_LINES_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}

export interface ClaimLineActionsContext {
  canApprove: boolean;
  canReject: boolean;
  canFlag: boolean;
  canDelete: boolean;
  /** Why Approve / Reject / Flag can't be used now, or null. */
  reviewLockReason: () => string | null;
  /** Why this line can't be deleted now, or null. */
  deleteLockReason: (row: GridRow) => string | null;
}

// The old command column, same order: Comment, Approve, Reject, Flag (reviewers), Delete (role rule).
export function buildClaimLineActionIcons(ctx: ClaimLineActionsContext): ActionIconDef[] {
  const reviewColor = (color: string) => () => (ctx.reviewLockReason() ? INACTIVE : color);
  const reviewLabel = (text: string) => () => ctx.reviewLockReason() ?? text;
  const icons: ActionIconDef[] = [
    // Not on a line that isn't saved yet; orange with comments, as before.
    { key: 'Comment', label: 'Comment', icon: COMMENT_ICON, hiddenFn: row => !row['id'],
      colorFn: row => ((row['lineCommentCount'] as number) > 0 ? ORANGE : BLACK) },
  ];
  if (ctx.canApprove) icons.push({ key: 'Approve', label: 'Approve', icon: CHECK_ICON, colorFn: reviewColor('#28a745'), disabledFn: () => !!ctx.reviewLockReason(), labelFn: reviewLabel('Approve') });
  if (ctx.canReject) icons.push({ key: 'Reject', label: 'Reject', icon: CROSS_ICON, colorFn: reviewColor('#dc3545'), disabledFn: () => !!ctx.reviewLockReason(), labelFn: reviewLabel('Reject') });
  if (ctx.canFlag) icons.push({ key: 'Flag', label: 'Flag', icon: FLAG_ICON, colorFn: reviewColor('#e0a800'), disabledFn: () => !!ctx.reviewLockReason(), labelFn: reviewLabel('Flag') });
  if (ctx.canDelete) icons.push({
    key: 'Delete', label: 'Delete', icon: TRASH_ICON,
    colorFn: row => (ctx.deleteLockReason(row) ? INACTIVE : BLACK),
    disabledFn: row => !!ctx.deleteLockReason(row), labelFn: row => ctx.deleteLockReason(row) ?? 'Delete',
  });
  return icons;
}
